import React, { useState } from 'react';
import I18n from 'modules/i18n';
import Modal from 'components/common/Modal';
import Icon from '@/components/ui/Icon';
import { UnstyledButton } from '@/components/ui/Buttons';
import styles from '@/components/admin/SlideShow/PresentMode/Navigator/Navigator.module.scss';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import Form from './Form';
import ButtonWithTooltip from './ButtonWithTooltip';

type SlideShow = {
  activityType?: string;
  assignable?: boolean;
  className?: string;
  id: number;
  isAssigned: boolean;
  is_module: boolean;
  lesson?: {
    id: number;
  },
  links: {
    deletePath?: string;
    editPath?: string;
    presentPath?: string;
    printUrl?: string;
    sharePath?: string;
    unfilteredPresentPath?: string;
    unsharePath?: string;
  };
  pacing?: number;
  program?: object;
  shareable?: boolean,
  shared?: boolean,
  slideShowId?: number,
  stafferId?: number,
  title?: string,
  useNewBuilder?: boolean,
};

interface ButtonProps {
  slideShow: SlideShow;
}

interface EditButtonProps extends ButtonProps {
  useButtonWithTooltip?: boolean;
  useNewBuilder?: boolean;
  lessonId?: string;
  programId?: string;
  fromSlideShow?: boolean;
  hideTabs?: boolean;
}

export const PresentButton = ({ slideShow }: ButtonProps) => {
  const querySeparator = slideShow.links.unfilteredPresentPath?.includes('?') ? '&' : '?';

  return (
    <ButtonWithTooltip
      openInNewWindow
      ariaLabel={`${I18n.t('present')} ${slideShow.title}`}
      className="tw-box-border tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7"
      link={`${slideShow.links.unfilteredPresentPath}${querySeparator}from=Lesson`}
      tooltipContent={I18n.t('present')}
    >
      <Icon icon="Play" size={16} />
    </ButtonWithTooltip>
  );
};

export const EditExistingSlideShowButton = ({ slideShow }: EditButtonProps) => (
  <ButtonWithTooltip
    ariaLabel={`${I18n.t('edit')} ${slideShow.title}`}
    className="tw-box-border tw-inline-flex tw-items-center tw-justify-center"
    link={`${slideShow.links.editPath}${slideShow.useNewBuilder ? '?new_builder=true' : ''}`}
    tooltipContent={I18n.t('edit/preview')}
  >
    <Icon icon="EditPreview" size={16} />
  </ButtonWithTooltip>
);

export const EditNewSlideShowButton = ({
  fromSlideShow,
  hideTabs,
  lessonId,
  programId,
  slideShow,
  useButtonWithTooltip = true,
  useNewBuilder,
}: EditButtonProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { teacherPacedMode } = useFlags();

  const openModal: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (useNewBuilder) event.preventDefault();
    if (!useNewBuilder) return;

    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  if (useButtonWithTooltip) {
    return (
      <>
        <ButtonWithTooltip
          ariaLabel={`${I18n.t('edit')} ${slideShow.title}`}
          className="tw-box-border tw-flex tw-justify-center tw-items-center tw-w-7 tw-h-7 tw-p-1"
          link={`${slideShow.links.editPath}${slideShow.useNewBuilder ? '?new_builder=true' : ''}`}
          tooltipContent={I18n.t('edit/preview')}
        >
          <Icon icon="EditPreview" size={16} />
        </ButtonWithTooltip>
        <Modal
          closeModal={closeModal}
          headerText="Save New Presentation"
          isOpen={modalIsOpen}
        >
          <Form
            button="edit"
            fromSlideShow={fromSlideShow}
            hideTabs={hideTabs}
            id={slideShow.id}
            lessonId={lessonId}
            programId={programId}
            slideShowId={slideShow.slideShowId}
            title={slideShow.title}
            type={slideShow.className}
            useNewBuilder={slideShow.useNewBuilder}
          />
        </Modal>
      </>
    );
  }

  return (
    <>
      <UnstyledButton
        aria-label={`${I18n.t('edit')} ${slideShow.title}`}
        className={cn(styles.settingsMenuRow, teacherPacedMode ? styles.teacherPacedUpdates : styles.legacy)}
        onClick={openModal}
      >
        <div className={styles.menuIconButton}>
          <Icon icon="EditPreview" size={16} />
        </div>
        <div>Edit Slideshow</div>
      </UnstyledButton>
      <Modal
        closeModal={closeModal}
        headerText="Save New Presentation"
        isOpen={modalIsOpen}
      >
        <Form
          button="edit"
          fromSlideShow={fromSlideShow}
          hideTabs={hideTabs}
          id={slideShow.id}
          lessonId={lessonId}
          programId={programId}
          slideShowId={slideShow.slideShowId}
          title={slideShow.title}
          type={slideShow.className}
          useNewBuilder={slideShow.useNewBuilder}
        />
      </Modal>
    </>
  );
};

// print icon for only TCI-created slide shows w/ guides
export const PrintButton = ({ slideShow }: ButtonProps) => {
  const link = slideShow.links.printUrl;
  if (!link) return null;

  return (
    <ButtonWithTooltip
      openInNewWindow
      ariaLabel={`${I18n.t('print')} ${slideShow.title}`}
      className="tw-box-border tw-flex tw-justify-center tw-items-center tw-w-7 tw-h-7"
      link={slideShow.links.printUrl}
      tooltipContent={I18n.t('print_guide')}
    >
      <Icon icon="Print" size={16} />
    </ButtonWithTooltip>
  );
};

// delete icon for only teacher slide shows
export const DeleteButton = ({ slideShow }: ButtonProps) => {
  const link = slideShow.links.deletePath;
  if (!link) return null;

  return (
    <ButtonWithTooltip
      ariaLabel={`${I18n.t('delete')} ${slideShow.title}`}
      className="tw-box-border tw-flex tw-justify-center tw-items-center"
      dataConfirm="Are you sure you want to delete this lesson guide?"
      dataMethod="delete"
      link={slideShow.links.deletePath}
      rel="nofollow"
      tooltipContent={I18n.t('delete')}
    >
      <Icon icon="Delete" size={16} />
    </ButtonWithTooltip>
  );
};
